import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ChevronRight, ClipboardList, Compass, Loader2, Users2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/contexts/SettingsContext";
import { ScriptureVerse } from "@/components/patria/ScriptureVerse";
import { ModuleTitleCOMPASS } from "@/components/compass/ModuleTitleCOMPASS";

const ROLE_LEVEL = { employee: 1, manager: 2, po_admin: 3, super_admin: 4 };

export default function CompassLanding() {
  const { user } = useAuth();
  const { settings: publicSettings } = useSettings();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [plans, setPlans] = useState([]);
  const [poQueue, setPoQueue] = useState({ entries: [], plans_awaiting_po: [] });
  const [myEntries, setMyEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const level = ROLE_LEVEL[user?.role || "employee"];

  useEffect(() => {
    (async () => {
      try {
        const reqs = [api.get("/compass/settings"), api.get("/compass/plans/mine")];
        if (level >= ROLE_LEVEL.manager) reqs.push(api.get("/compass/entry-requests/mine"));
        if (level >= ROLE_LEVEL.po_admin) reqs.push(api.get("/compass/entry-requests/queue"));
        const results = await Promise.all(reqs);
        setSettings(results[0].data);
        setPlans(results[1].data);
        if (level >= ROLE_LEVEL.manager) setMyEntries(results[2].data || []);
        if (level >= ROLE_LEVEL.po_admin) setPoQueue(results[level >= ROLE_LEVEL.manager ? 3 : 2].data);
      } catch (err) {
        toast.error(formatApiErrorDetail(err?.response?.data?.detail));
      } finally {
        setLoading(false);
      }
    })();
  }, [level]);

  if (loading || !settings) {
    return (
      <div className="flex items-center gap-2 text-charcoal-muted">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading COMPASS…
      </div>
    );
  }

  const myOwnPlan = plans.find((p) => p.employee_id === user.id);
  const managerPlans = plans.filter((p) => p.manager_id === user.id);

  return (
    <div className="space-y-8" data-testid="compass-landing">
      {/* Hero */}
      <section>
        <ModuleTitleCOMPASS />
        <h1 className="mt-3 text-4xl sm:text-5xl font-serif text-forest-900 leading-tight">
          {settings.tagline}
        </h1>
        <p className="mt-3 max-w-3xl text-charcoal-soft">
          COMPASS is Patria&rsquo;s structured process for closing a persistent performance gap —
          used <em>only</em> after direct feedback has been given and the gap has not changed.
          It is not goal-setting, and it is not for spiritual growth or covenant matters,
          which we walk through pastorally on a separate track.
        </p>
      </section>

      {publicSettings?.verses_enabled && <ScriptureVerse />}

      {/* Acronym cards */}
      <section>
        <h2 className="text-2xl font-serif text-forest-900 mb-4">What COMPASS Stands For</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {settings.acronym.map((a, i) => (
            <div key={a.key} className="patria-card p-5" data-testid={`compass-acronym-${a.key}`}>
              <span
                className={`inline-flex h-11 w-11 rounded-full items-center justify-center text-cream-100 font-serif text-xl ${
                  i % 2 === 0 ? "bg-forest-600" : "bg-terracotta-500"
                }`}
              >
                {a.letter}
              </span>
              <h3 className="mt-3 text-lg font-serif text-forest-900">{a.label}</h3>
              <p className="mt-1 text-sm text-charcoal-soft">{a.definition}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Fairness panel */}
      <section>
        <h2 className="text-2xl font-serif text-forest-900 mb-4">Fairness By Design</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {settings.fairness_blocks.map((b) => (
            <div key={b.key} className="patria-card p-5" data-testid={`compass-fairness-${b.key}`}>
              <h3 className="font-serif text-lg text-forest-900">{b.heading}</h3>
              <p className="mt-1 text-sm text-charcoal-soft leading-relaxed">{b.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Role-aware calls to action */}
      {myOwnPlan && (
        <section>
          <h2 className="text-2xl font-serif text-forest-900 mb-3">Your Plan</h2>
          <PlanRow plan={myOwnPlan} onOpen={() => navigate(`/compass/plans/${myOwnPlan.id}`)} />
        </section>
      )}

      {level >= ROLE_LEVEL.manager && (
        <section>
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-serif text-forest-900">Manager Actions</h2>
            <button
              className="patria-btn-primary"
              onClick={() => navigate("/compass/entry-request/new")}
              data-testid="compass-new-entry-btn"
            >
              <ClipboardList className="h-4 w-4" strokeWidth={1.5} /> New entry request
            </button>
          </div>
          <p className="text-sm text-charcoal-muted mt-1">
            Managers cannot build a plan directly — start an entry request and People
            Operations will review before plan-building unlocks.
          </p>
          {managerPlans.length > 0 && (
            <div className="mt-4 space-y-3">
              {managerPlans.map((p) => (
                <PlanRow key={p.id} plan={p} onOpen={() => navigate(`/compass/plans/${p.id}`)} />
              ))}
            </div>
          )}
          {myEntries.length > 0 && (
            <div className="mt-4 patria-card p-4">
              <h3 className="text-lg font-serif text-forest-900">Your Entry Requests</h3>
              <ul className="mt-2 divide-y divide-sage-300/60">
                {myEntries.map((e) => (
                  <li key={e.id} className="py-3 flex items-center gap-3">
                    <span
                      className={
                        e.status === "approved" ? "patria-chip-green"
                          : e.status === "denied" ? "patria-chip-terra"
                          : "patria-chip-neutral"
                      }
                    >
                      {e.status.replace("_", " ")}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-forest-900">{e.employee_name}</p>
                      <p className="text-xs text-charcoal-muted">{e.gap_category?.replace("_", " ")}</p>
                    </div>
                    {e.status === "approved" && (
                      <button
                        className="patria-btn-secondary"
                        onClick={() => navigate(`/compass/entry-requests/${e.id}/build-plan`)}
                      >
                        Build plan <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}

      {level >= ROLE_LEVEL.po_admin && (
        <section>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              className="patria-card patria-card-hover p-6 text-left"
              onClick={() => navigate("/compass/po-queue")}
              data-testid="compass-po-queue-link"
            >
              <span className="patria-chip-terra">People Operations</span>
              <h3 className="mt-3 text-xl font-serif text-forest-900">
                Review Queue · {poQueue.entries.length + poQueue.plans_awaiting_po.length}
              </h3>
              <p className="mt-1 text-sm text-charcoal-soft">
                Concur on entry requests and approve plans awaiting review.
              </p>
            </button>
            <button
              className="patria-card patria-card-hover p-6 text-left"
              onClick={() => navigate("/compass/calibration")}
              data-testid="compass-calibration-link"
            >
              <span className="patria-chip-green">Calibration</span>
              <h3 className="mt-3 text-xl font-serif text-forest-900">Calibration Dashboard</h3>
              <p className="mt-1 text-sm text-charcoal-soft">
                All plans across all 5 campuses, by status, duration, category, and outcome.
              </p>
            </button>
          </div>
        </section>
      )}
    </div>
  );
}

function PlanRow({ plan, onOpen }) {
  return (
    <button
      onClick={onOpen}
      className="patria-card patria-card-hover w-full p-5 flex items-center gap-4 text-left"
      data-testid={`compass-plan-row-${plan.id}`}
    >
      <span className="h-11 w-11 rounded-full bg-forest-600 text-cream-100 flex items-center justify-center">
        <Compass className="h-5 w-5" strokeWidth={1.5} />
      </span>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-forest-900">{plan.employee_name}</p>
        <p className="text-xs text-charcoal-muted">
          {plan.gap_category?.replace("_", " ")} · {plan.timeline_days}-day timeline
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          <span
            className={
              plan.status === "in_progress" || plan.status === "acknowledged" || plan.status === "approved" || plan.status === "delivered"
                ? "patria-chip-green"
                : plan.status === "paused"
                ? "patria-chip-terra"
                : "patria-chip-neutral"
            }
          >
            {plan.status.replace("_", " ")}
          </span>
        </div>
      </div>
      <ChevronRight className="h-5 w-5 text-forest-600" strokeWidth={1.5} />
    </button>
  );
}
